import {
  editableFormPayload,
  type GeneratedFormDefinition,
  type GeneratedFormField,
  type GeneratedFormValues,
} from '@template-ui/helpers/generated-form.helpers';
import { resolveWorkflowFormLayout, type WorkflowFormLayout } from './workflow-form-layout';
import { isWorkflowFormLookupBinding, type WorkflowFormLookupBinding, type WorkflowFormLookupAnswer } from './workflow-form-lookup-contract';

export type WorkflowCaptureValue = string | number | boolean;
export interface WorkflowCaptureCondition {
  fieldId: string;
  equals: WorkflowCaptureValue;
}
export interface WorkflowCaptureField {
  id: string;
  label: string;
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'integer'
    | 'date'
    | 'boolean'
    | 'select';
  required: boolean;
  maxLength?: number | null;
  minimum?: number | null;
  maximum?: number | null;
  options?: { value: string; label: string }[] | null;
  visibleWhen?: WorkflowCaptureCondition | null;
  layout?: WorkflowFormLayout | null;
  lookup?: WorkflowFormLookupBinding | null;
}
export interface WorkflowCaptureForm {
  version: 1 | 2 | 3;
  id: string;
  title: string;
  fields: WorkflowCaptureField[];
}
export interface WorkflowFormSubmission {
  id: number;
  occurrenceId: number;
  formId: string;
  schemaHash: string;
  submittedUtc: string;
  submittedByUserId: number;
  stepLabel?: string;
  occurrenceNumber?: number;
  form: WorkflowCaptureForm;
  values: GeneratedFormValues | null;
  masked: boolean;
  lookupEvidence?: Record<string, WorkflowFormLookupAnswer> | null;
}

export const captureFormContract = {
  contract: 'WorkflowFormSubmission',
  version: 1,
  fields: [],
} satisfies GeneratedFormDefinition;
const identifier = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
const text = (value: unknown, maximum: number): value is string =>
  typeof value === 'string' && !!value.trim() && value.length <= maximum;
const scalarTypes = [
  'text',
  'textarea',
  'number',
  'integer',
  'date',
  'boolean',
  'select',
];
const numberBound = (value: unknown) =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  Math.abs(value) <= 1e15;
const unknownKeys = (value: object, allowed: readonly string[]) => Object.keys(value).some(key => !allowed.includes(key));

/** No runtime code or arbitrary property paths are accepted from an authored form. */
export function captureFormDefinition(
  form: WorkflowCaptureForm | null | undefined,
): GeneratedFormDefinition | null {
  if (
    !form ||
    unknownKeys(form, ['version', 'id', 'title', 'fields']) ||
    ![1, 2, 3].includes(form.version) ||
    !text(form.id, 80) ||
    !identifier.test(form.id) ||
    !text(form.title, 120) ||
    !Array.isArray(form.fields) ||
    !form.fields.length ||
    form.fields.length > 50
  )
    return null;
  const layouts = resolveWorkflowFormLayout(form.version, form.fields.map(field => ({
    control: field?.type === 'boolean' ? 'checkbox' : field?.type,
    layout: field?.layout,
  })));
  if (!layouts) return null;
  const fields: GeneratedFormField[] = [],
    installed = new Map<string, WorkflowCaptureField>();
  for (const [index, field] of form.fields.entries()) {
    if (
      !field ||
      unknownKeys(field, ['id', 'label', 'type', 'required', 'maxLength', 'minimum', 'maximum', 'options', 'visibleWhen', 'layout', 'lookup']) ||
      !text(field.id, 80) ||
      !identifier.test(field.id) ||
      field.id === 'constructor' ||
      installed.has(field.id) ||
      !text(field.label, 120) ||
      !scalarTypes.includes(field.type) ||
      typeof field.required !== 'boolean'
    )
      return null;
    const numeric = field.type === 'number' || field.type === 'integer';
    if (
      field.maxLength != null &&
      (!['text', 'textarea'].includes(field.type) ||
        !Number.isSafeInteger(field.maxLength) ||
        field.maxLength < 1 ||
        field.maxLength > 100000)
    )
      return null;
    if (
      [field.minimum, field.maximum].some(
        (bound) =>
          bound != null &&
          (!numeric ||
            !numberBound(bound) ||
            (field.type === 'integer' && !Number.isInteger(bound))),
      )
    )
      return null;
    if (
      field.minimum != null &&
      field.maximum != null &&
      field.minimum > field.maximum
    )
      return null;
    if (field.type === 'select') {
      if (field.lookup != null) {
        if (form.version !== 3 || !isWorkflowFormLookupBinding(field.lookup) || field.options != null) return null;
      } else if (
        !Array.isArray(field.options) ||
        !field.options.length ||
        field.options.length > 100 ||
        field.options.some(
          (option) =>
            !option || unknownKeys(option, ['value', 'label']) || !text(option.value, 120) || !text(option.label, 120),
        ) ||
        new Set(field.options.map((option) => option.value)).size !==
          field.options.length
      )
        return null;
    } else if (field.options != null || field.lookup != null) return null;
    if (field.visibleWhen) {
      const condition = field.visibleWhen,
        parent = installed.get(condition.fieldId);
      if (unknownKeys(condition, ['fieldId', 'equals']) || !parent || !valueFitsField(parent, condition.equals)) return null;
    }
    installed.set(field.id, field);
    fields.push({
      name: field.id,
      label: field.label,
      labelToken: '',
      required: field.required,
      nullable: !field.required,
      type: numeric
        ? field.type
        : field.type === 'boolean'
          ? 'boolean'
          : 'string',
      control: numeric
        ? 'number'
        : field.type === 'boolean'
          ? 'checkbox'
          : field.type === 'date'
            ? 'date'
            : field.type,
      maxLength: ['text', 'textarea'].includes(field.type)
        ? (field.maxLength ?? 4000)
        : undefined,
      minimum: numeric ? (field.minimum ?? -1e15) : undefined,
      maximum: numeric ? (field.maximum ?? 1e15) : undefined,
      options:
        field.type === 'select'
          ? field.options?.map((option) => ({ ...option, labelToken: '' })) ?? []
          : undefined,
      dateSemantics: field.type === 'date' ? 'calendar-date' : undefined,
      visibleWhen: field.visibleWhen
        ? { field: field.visibleWhen.fieldId, equals: field.visibleWhen.equals }
        : undefined,
      errorCodes: [`workflow.form.capture.${field.id}`],
      ...(form.version !== 1 ? layouts[index] : {}),
    });
  }
  return { ...captureFormContract, fields, ...(form.version !== 1 ? { stackOnNarrow: true } : {}) };
}

function valueFitsField(field: WorkflowCaptureField, value: unknown): boolean {
  if (field.type === 'boolean') return typeof value === 'boolean';
  if (field.type === 'number' || field.type === 'integer')
    return (
      numberBound(value) &&
      (field.type !== 'integer' || Number.isSafeInteger(value)) &&
      (value as number) >= (field.minimum ?? -1e15) &&
      (value as number) <= (field.maximum ?? 1e15)
    );
  if (field.type === 'select')
    return field.lookup != null ? typeof value === 'string' && !!value.trim() && value === value.trim() && value.length <= 120
      : field.options?.some((option) => option.value === value) ?? false;
  if (field.type === 'date')
    return (
      typeof value === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(value) &&
      !value.startsWith('0000-') &&
      !Number.isNaN(Date.parse(value)) &&
      new Date(value).toISOString().slice(0, 10) === value
    );
  return (
    typeof value === 'string' &&
    !!value.trim() &&
    value.length <= (field.maxLength ?? 4000)
  );
}

export function captureFormValues(
  definition: GeneratedFormDefinition,
  values: GeneratedFormValues,
): Record<string, unknown> {
  return editableFormPayload(definition, values);
}

export function captureAnswerLabel(
  field: WorkflowCaptureField,
  value: unknown,
): string {
  if (value === undefined || value === null || value === '')
    return 'Not provided';
  if (field.type === 'boolean') return value === true ? 'Yes' : 'No';
  if (field.type === 'select')
    return (
      field.options?.find((option) => option.value === value)?.label ??
      String(value)
    );
  return String(value);
}
